<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onKeyStroke } from '@vueuse/core'
import { useNotificationsStore } from '@/stores/notifications'
import type { NotificationRow } from '@/stores/notifications'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'toggle'): void; (e: 'close'): void }>()

const { t, te, locale } = useI18n()
const store = useNotificationsStore()

// Dot colour per level — mirrors the Figma design tokens
// (Primary #D81B5D / success / danger).
const DOT_COLOR: Record<string, string> = {
  info: '#D81B5D',
  success: '#57C86F',
  error: '#F8574C',
}

function dotColor(level: string): string {
  return DOT_COLOR[level] ?? DOT_COLOR.info
}

// Prefer the locale-reactive i18n message keyed by the event; fall back to the
// server-rendered message when no key exists for the event.
function displayText(n: NotificationRow): string {
  const key = `notifications.event.${n.event}`
  if (te(key)) {
    return t(key, {
      workspace: n.data?.workspace ?? '',
      requester: n.data?.requester ?? '',
    })
  }

  return n.message
}

// Compact relative time matching the design ("3 min", "15 min", "2 h", "5 d").
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then))
    return ''

  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (diffSec < 60)
    return t('notifications.now')

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60)
    return `${diffMin} min`

  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24)
    return `${diffH} h`

  const diffD = Math.floor(diffH / 24)
  if (diffD < 7)
    return `${diffD} d`

  return new Date(iso).toLocaleDateString(locale.value)
}

function onItemClick(n: NotificationRow) {
  store.markRead(n.id)
}

// Refresh the list each time the dropdown opens so it reflects the latest
// server state (the badge is kept fresh by the store's background poll).
watch(
  () => props.open,
  open => {
    if (open)
      store.fetch()
  },
)

// Escape closes the dropdown while it's open.
onKeyStroke('Escape', () => {
  if (props.open)
    emit('close')
})
</script>

<template>
  <div class="relative flex">
    <button
      type="button"
      class="relative w-[29px] h-[29px] p-0 border-0 bg-transparent inline-flex items-center justify-center cursor-pointer text-[#d81b5d]"
      :aria-label="$t('notifications.ariaLabel')"
      :aria-expanded="open ? 'true' : 'false'"
      @click="emit('toggle')"
    >
      <svg
        class="w-[22px] h-[22px]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <span
        v-if="store.unreadCount > 0"
        class="absolute top-[1px] right-[2px] w-[9px] h-[9px] rounded-full bg-[#d81b5d] border-2 border-white"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      class="notif-panel absolute top-[130%] right-0 z-50 w-[320px] max-w-[90vw] bg-white border border-[#c1c1cc] rounded-[12px] shadow-[9px_8px_32px_0px_rgba(0,0,0,0.1)] py-[14px] ps-[14px] pe-[16px] flex flex-col gap-[10px]"
    >
      <div class="flex items-center justify-between gap-4">
        <span class="font-['Inter'] font-bold text-[14px] leading-none text-[#101010]">
          {{ $t('notifications.title') }}
        </span>
        <button
          v-if="store.items.length > 0"
          type="button"
          class="border-0 bg-transparent p-0 cursor-pointer font-['Inter'] text-[11px] leading-none text-[#6c757d] hover:text-[#101010]"
          @click="store.clearAll()"
        >
          {{ $t('notifications.clearAll') }}
        </button>
      </div>

      <div class="h-px w-full bg-[#E5E8ED]" />

      <div
        v-if="store.items.length === 0"
        class="py-3 text-center font-['Inter'] text-[12px] text-[#6c757d]"
      >
        {{ $t('notifications.empty') }}
      </div>

      <ul
        v-else
        class="list-none m-0 p-0 flex flex-col max-h-[320px] overflow-y-auto"
      >
        <li
          v-for="(n, index) in store.items"
          :key="n.id"
          class="notif-row flex items-start gap-[10px] py-[9px] px-[6px] cursor-pointer"
          :class="[
            { 'notif-row--unread': !n.is_read },
            index !== store.items.length - 1 ? 'border-b border-[#E5E8ED]' : '',
          ]"
          @click="onItemClick(n)"
        >
          <span
            class="mt-[5px] shrink-0 w-[9px] h-[9px] rounded-full"
            :style="{ backgroundColor: dotColor(n.level), opacity: n.is_read ? 0.45 : 1 }"
          />
          <span
            class="flex-1 min-w-0 font-['Inter'] text-[12.5px] leading-[1.35] break-words"
            :class="n.is_read ? 'text-[#6c757d] font-normal' : 'text-[#101010] font-medium'"
          >
            {{ displayText(n) }}
          </span>
          <span
            class="shrink-0 font-['Inter'] text-[10px] leading-none text-[#c1c1cc] whitespace-nowrap pt-[2px]"
          >
            {{ relativeTime(n.created_at) }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.notif-row--unread {
  background-color: #fdeef4;
}

.notif-row:hover {
  background-color: #f6f7f9;
}
</style>
